import { BrandStyleGuide } from '../types';

/**
 * The default brand style guide (Frontend Fallback).
 * Used if no dynamic style is provided.
 */
export const defaultBrandStyle: BrandStyleGuide = {
    companyName: '4HVNSK8',

    logo: {
        url: 'https://hvnsk8.web.app/assets/logo/4HVNSK8-logo-black.png',
        alt: '4HVNSK8',
        height: 40,
        invertOnDark: true,
    },

    siteUrl: 'https://hvnsk8.web.app',

    colors: {
        headerBg:   '#111111',
        footerBg:   '#111111',
        bodyBg:     '#f4f4f4',
        cardBg:     '#ffffff',
        primary:    '#111111',
        bodyText:   '#333333',
        mutedText:  '#666666',
        subtleText: '#555555',
        linkText:   '#999999',
        border:     '#dddddd',
        surfaceBg:  '#f9f9f9',
        buttonBg:   '#111111',
        buttonText: '#ffffff',
    },

    typography: {
        fontFamily:     "'Helvetica Neue', Arial, sans-serif",
        headingSize:    22,
        bodySize:       16,
        smallSize:      12,
        tinySize:       11,
        lineHeight:     1.6,
        letterSpacing:  '2px',
    },

    layout: {
        containerWidth: 600,
        headerPadding:  '30px 20px',
        bodyPadding:    '40px 30px',
        footerPadding:  '25px 20px',
        borderRadius:   4,
    },

    socials: [
        { label: 'Instagram', url: 'https://www.instagram.com/4hvnsk8' },
        { label: 'Twitter',   url: 'https://x.com/4hvnsk8' },
        { label: 'YouTube',   url: 'https://www.youtube.com/@4hvnsk8' },
    ],
};

// ─── Internal helpers (read from style) ────────────────────────────────

interface FooterOptions {
    includeUnsubscribe: boolean;
    unsubscribeUrl?: string;
    includeSocial: boolean;
    style: BrandStyleGuide;
}

const buildFooter = (opts: FooterOptions): string => {
    const s = opts.style;
    const year = new Date().getFullYear();

    const socialBlock = opts.includeSocial && s.socials.length > 0
        ? `<p style="margin: 0 0 15px 0;">
                ${s.socials.map(
                    (link) =>
                        `<a href="${link.url}" style="color: ${s.colors.linkText}; text-decoration: none; margin: 0 8px; font-size: ${s.typography.smallSize}px;">${link.label}</a>`
                ).join('\n                ')}
              </p>`
        : '';

    const unsubscribeBlock = opts.includeUnsubscribe && opts.unsubscribeUrl
        ? `<p style="margin: 0 0 10px 0;">
                <a href="${opts.unsubscribeUrl}" style="color: ${s.colors.mutedText}; font-size: ${s.typography.tinySize}px; text-decoration: underline;">Unsubscribe</a>
              </p>`
        : '';

    return `          <tr>
            <td style="background-color: ${s.colors.footerBg}; padding: ${s.layout.footerPadding}; text-align: center;">
              ${socialBlock}
              ${unsubscribeBlock}
              <p style="color: ${s.colors.subtleText}; font-size: ${s.typography.tinySize}px; margin: 0;">
                &copy; ${year} ${s.companyName}. All rights reserved.
              </p>
            </td>
          </tr>`;
};

// ─── Public helpers ─────────────────────────────────────────────────────────

export interface BaseTemplateOptions {
    /** The inner HTML body content (goes between header and footer) */
    body: string;
    /** Show social links in footer (default: true) */
    includeSocial?: boolean;
    /** Show unsubscribe link in footer (default: false for transactional) */
    includeUnsubscribe?: boolean;
    /** The unsubscribe URL, required when includeUnsubscribe is true */
    unsubscribeUrl?: string;
    /** Custom brand style (default: built-in defaultBrandStyle) */
    style?: BrandStyleGuide;
}

/**
 * Wrap any body content in the unified brand email layout.
 * Use this for every outbound email to ensure visual consistency.
 */
export const wrapInBaseTemplate = (opts: BaseTemplateOptions): string => {
    const s = opts.style || defaultBrandStyle;
    
    // Check if s.logo exists (backward compatibility safety)
    const logoUrl = s.logo?.url || defaultBrandStyle.logo.url;
    const logoAlt = s.logo?.alt || defaultBrandStyle.logo.alt;
    const logoHeight = s.logo?.height || defaultBrandStyle.logo.height;
    const invertOnDark = s.logo?.invertOnDark ?? defaultBrandStyle.logo.invertOnDark;
    
    const logoFilter = invertOnDark ? 'filter: invert(1);' : '';

    const emailHead = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <title>${s.companyName}</title>
</head>`;

    const bodyOpen = `<body style="margin: 0; padding: 0; font-family: ${s.typography.fontFamily}; background-color: ${s.colors.bodyBg}; -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%;">
  <table role="presentation" style="width: 100%; border-collapse: collapse;">
    <tr>
      <td align="center" style="padding: 40px 0;">
        <table role="presentation" style="width: ${s.layout.containerWidth}px; max-width: ${s.layout.containerWidth}px; border-collapse: collapse; background-color: ${s.colors.cardBg};">`;

    const header = `          <tr>
            <td style="background-color: ${s.colors.headerBg}; padding: ${s.layout.headerPadding}; text-align: center;">
              <a href="${s.siteUrl}" style="text-decoration: none;">
                <img src="${logoUrl}" alt="${logoAlt}" style="height: ${logoHeight}px; width: auto; ${logoFilter}" />
              </a>
            </td>
          </tr>`;

    const footer = buildFooter({
        includeSocial: opts.includeSocial ?? true,
        includeUnsubscribe: opts.includeUnsubscribe ?? false,
        unsubscribeUrl: opts.unsubscribeUrl,
        style: s
    });

    const tableClose = `        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;

    return `${emailHead}
${bodyOpen}
${header}
          <tr>
            <td style="padding: ${s.layout.bodyPadding};">
${opts.body}
            </td>
          </tr>
${footer}
${tableClose}`;
};
